/**
 * Shared components for the Thai sales document pages (Quotations / Billing Notes / Tax Invoices).
 * All pages use Invoice-style full-screen overlay form, multi-tax scheme, product autocomplete,
 * per-row loading, and #1e3a8a navy brand color to match InvoicesPage.
 */
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";
import { blockInvalidNumberKey } from "../utils/validate";
import { formatDate } from "../utils/dateFormat";
import {
  getSalesDocs, getSalesDoc, createSalesDoc, updateSalesDoc,
  issueSalesDoc, approveSalesDoc, convertSalesDoc, markSalesDocPaid,
  voidSalesDoc, deleteSalesDoc, downloadSalesDocPdf, downloadSalesDocWhtCertificate, duplicateSalesDoc,
  getTenantCurrency, getCurrencies, getProducts, getClients,
} from "../api";

// ── Constants ────────────────────────────────────────────────────────────────

const FINANCE_PRIMARY = "#1e3a8a";

export const TYPE_LABELS: Record<string, { short: string; label: string; color: string; bg: string }> = {
  tax_invoice:  { short: "TI", label: "Tax Invoice",  color: "#c2410c", bg: "#fff7ed" },
  billing_note: { short: "BN", label: "Billing Note", color: "#1d4ed8", bg: "#eff6ff" },
  quotation:    { short: "QT", label: "Quotation",    color: "#7e22ce", bg: "#faf5ff" },
  receipt:      { short: "RC", label: "Receipt",      color: "#166534", bg: "#dcfce7" },
  credit_note:  { short: "CN", label: "Credit Note",  color: "#dc2626", bg: "#fef2f2" },
  debit_note:   { short: "DN", label: "Debit Note",   color: "#0f766e", bg: "#f0fdfa" },
};

export const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft:     { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  sent:      { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  approved:  { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  paid:      { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  converted: { bg: "#f3e8ff", text: "#7e22ce", border: "#d8b4fe" },
  void:      { bg: "#f8f8f8", text: "#94a3b8", border: "#e2e8f0" },
};

const TAX_SCHEMES: Record<string, {
  label: string;
  taxes: { name: string; rate: string }[];
  wht_label: string;
  wht_options: { label: string; rate: string }[];
}> = {
  custom:     { label: "Custom",             taxes: [],                                                          wht_label: "TDS / WHT", wht_options: [] },
  thailand:   { label: "🇹🇭 Thailand",        taxes: [{ name: "VAT", rate: "7" }],                               wht_label: "WHT",       wht_options: [{ label: "Professional Services", rate: "3" }, { label: "Advertising", rate: "2" }, { label: "Rent", rate: "5" }, { label: "Transport", rate: "1" }] },
  india:      { label: "🇮🇳 India",           taxes: [{ name: "CGST", rate: "9" }, { name: "SGST", rate: "9" }], wht_label: "TDS",       wht_options: [{ label: "Professional/Technical (194J)", rate: "10" }, { label: "Contractor (194C)", rate: "1" }, { label: "Rent (194I)", rate: "10" }, { label: "Commission (194H)", rate: "5" }] },
  singapore:  { label: "🇸🇬 Singapore",       taxes: [{ name: "GST", rate: "9" }],                               wht_label: "WHT",       wht_options: [{ label: "Professional Services", rate: "15" }, { label: "Royalties", rate: "10" }] },
  uk:         { label: "🇬🇧 United Kingdom",  taxes: [{ name: "VAT", rate: "20" }],                              wht_label: "WHT",       wht_options: [] },
  uae:        { label: "🇦🇪 UAE",             taxes: [{ name: "VAT", rate: "5" }],                               wht_label: "WHT",       wht_options: [] },
  australia:  { label: "🇦🇺 Australia",       taxes: [{ name: "GST", rate: "10" }],                              wht_label: "WHT",       wht_options: [] },
  zero_rated: { label: "Zero-rated / Export", taxes: [{ name: "VAT (Zero-rated)", rate: "0" }],                  wht_label: "WHT",       wht_options: [] },
};

export const BLANK_FORM = {
  client_id: "", currency: "", currency_symbol: "",
  issue_date: new Date().toISOString().split("T")[0],
  due_date: "", valid_until: "", credit_days: "",
  project_name: "", tax_scheme: "",
  taxes: [{ name: "VAT", rate: "7" }] as { name: string; rate: string }[],
  wht_rate: "", wht_label: "WHT",
  discount: "0", notes: "",
  line_items: [{ description: "", quantity: "1", unit_price: "", amount: "", detail: "" }] as {
    description: string; quantity: string; unit_price: string; amount: string; detail: string;
  }[],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmt(amount: number, symbol = "฿") {
  return `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function downloadCsvBlob(filename: string, rows: any[][]) {
  const csv = rows.map(r => r.map((v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character (e.g. the em dash used for a blank field) as mojibake.
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/** CSV export for any sales-doc list (Quotations/Billing Notes/Tax Invoices/Receipts/Credit&Debit Notes). */
export function exportSalesDocsCsv(docType: string, docs: any[]) {
  const label = TYPE_LABELS[docType]?.label || docType;
  const headers = [`${label} No.`, "Client", "Issue Date", "Due/Valid Date", "Total", "Currency", "Status"];
  const rows = docs.map(d => [
    d.doc_number || "",
    d.client_name || "",
    d.issue_date || "",
    d.due_date || d.valid_until || "",
    d.total ?? "",
    d.currency || "",
    d.status || "",
  ]);
  downloadCsvBlob(`${docType}.csv`, [headers, ...rows]);
}

function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

// ── Small UI pieces ──────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 99,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`, textTransform: "capitalize" }}>
      {status}
    </span>
  );
}

export function TypeBadge({ type }: { type: string }) {
  const t = TYPE_LABELS[type];
  if (!t) return null;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 6,
      background: t.bg, color: t.color, border: `1px solid ${t.color}22` }}>
      {t.short}
    </span>
  );
}

// ── DocListTab (generic, configured by props) ────────────────────────────────

export type ConvertOption = { label: string; target: string; color?: string };

export function DocListTab({
  docType,
  clients,
  convertOptions = [],
  canMarkPaid = false,
  canApprove = false,
  readOnly = false,
  allowVoid,
}: {
  docType: "tax_invoice" | "billing_note" | "quotation" | "receipt";
  clients: any[];
  convertOptions?: ConvertOption[];
  canMarkPaid?: boolean;
  canApprove?: boolean;
  readOnly?: boolean;
  /** Show a Void-only menu even when readOnly (e.g. Receipts: no edit/duplicate/delete, but void for corrections). Defaults to !readOnly. */
  allowVoid?: boolean;
}) {
  const canShowVoid = allowVoid ?? !readOnly;
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();

  // ── Form visibility ──
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  // ── Table controls ──
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("issue_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Per-row action loading: tracks { id, action } to show loading only on the clicked button
  const [loadingAction, setLoadingAction] = useState<{ id: number; action: string } | null>(null);
  const [editFetching, setEditFetching] = useState(false);
  const isRowLoading = (id: number, action: string) =>
    loadingAction?.id === id && loadingAction?.action === action;

  // ── Queries ──
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["sales-docs", docType, statusFilter],
    queryFn: () => getSalesDocs({ doc_type: docType, ...(statusFilter ? { status: statusFilter } : {}) }),
  });
  const { data: tc } = useQuery({ queryKey: ["tenantCurrency"], queryFn: getTenantCurrency });
  const { data: currencies = [] } = useQuery({ queryKey: ["currencies"], queryFn: getCurrencies });
  const { data: allProducts = [] } = useQuery({ queryKey: ["products"], queryFn: () => getProducts() });
  // Use clients from prop (already fetched by parent) but also ensure clients are fresh
  const { data: freshClients = [] } = useQuery({ queryKey: ["clients"], queryFn: getClients });
  const effectiveClients = (freshClients as any[]).length > 0 ? freshClients : clients;

  const symbol = (tc as any)?.currency_symbol || "฿";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["sales-docs"] });

  // ── Mutations ──
  const createMut = useMutation({
    mutationFn: createSalesDoc,
    onSuccess: () => { invalidate(); setShowForm(false); toast(`${TYPE_LABELS[docType].label} created`, "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to create", "error"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateSalesDoc(id, data),
    onSuccess: () => { invalidate(); setShowForm(false); setEditing(null); toast("Updated", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to update", "error"),
  });

  const issueMut = useMutation({
    mutationFn: issueSalesDoc,
    onSuccess: () => { invalidate(); toast("Marked as Sent", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const approveMut = useMutation({
    mutationFn: approveSalesDoc,
    onSuccess: () => { invalidate(); toast("Quotation approved", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const convertMut = useMutation({
    mutationFn: ({ id, target }: { id: number; target: string }) => convertSalesDoc(id, target),
    onSuccess: (result: any) => {
      invalidate();
      const label = TYPE_LABELS[result.doc_type]?.label || result.doc_type;
      toast(`Converted → ${label} ${result.doc_number}`, "success");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to convert", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const paidMut = useMutation({
    mutationFn: markSalesDocPaid,
    onSuccess: () => { invalidate(); toast("Marked as Paid — Receipt generated", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const voidMut = useMutation({
    mutationFn: voidSalesDoc,
    onSuccess: () => { invalidate(); toast("Document voided", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to void", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const dupMut = useMutation({
    mutationFn: duplicateSalesDoc,
    onSuccess: () => { invalidate(); toast("Duplicated as new draft", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to duplicate", "error"),
    onSettled: () => setLoadingAction(null),
  });

  const deleteMut = useMutation({
    mutationFn: deleteSalesDoc,
    onSuccess: () => { invalidate(); toast("Deleted", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to delete", "error"),
    onSettled: () => setLoadingAction(null),
  });

  // ── Handlers ──
  const openEdit = async (doc: any) => {
    setEditFetching(true);
    try {
      const full = await getSalesDoc(doc.id);
      setEditing(full);
      setShowForm(true);
      setOpenMenuId(null);
    } finally {
      setEditFetching(false);
    }
  };

  const handleDownload = async (doc: any) => {
    setLoadingAction({ id: doc.id, action: "pdf" });
    try {
      const { blob, filename } = await downloadSalesDocPdf(doc.id, doc.doc_number);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      invalidate();
    } catch { toast("Failed to download PDF", "error"); }
    finally { setLoadingAction(null); }
  };

  const handleDownloadWht = async (doc: any) => {
    setLoadingAction({ id: doc.id, action: "wht" });
    try {
      const { blob, filename } = await downloadSalesDocWhtCertificate(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to download WHT certificate", "error"); }
    finally { setLoadingAction(null); }
  };

  const handleConvert = async (doc: any, target: string, targetLabel: string) => {
    const ok = await confirm(`Convert ${doc.doc_number} to a ${targetLabel}? The original will be marked as converted.`);
    if (!ok) return;
    setLoadingAction({ id: doc.id, action: `convert-${target}` });
    convertMut.mutate({ id: doc.id, target });
  };

  const handleVoid = async (doc: any) => {
    const ok = await confirm(`Void ${doc.doc_number}? The document number will be retained for audit but the document will be permanently inactive.`);
    if (!ok) return;
    setLoadingAction({ id: doc.id, action: "void" });
    voidMut.mutate(doc.id);
  };

  const handleDelete = async (doc: any) => {
    const ok = await confirm(`Delete draft ${doc.doc_number}? This cannot be undone.`);
    if (!ok) return;
    setLoadingAction({ id: doc.id, action: "delete" });
    deleteMut.mutate(doc.id);
  };

  const openMenu = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(id);
  };

  // ── Derived data ──
  const typeLabel = TYPE_LABELS[docType].label;

  const statusOptions: string[] =
    docType === "tax_invoice" ? ["draft", "sent", "void"] :
    docType === "receipt"     ? ["paid", "void"] :
    docType === "quotation"   ? ["draft", "sent", "approved", "converted", "void"] :
                                ["draft", "sent", "converted", "void"];

  const canConvert = (status: string) => ["draft", "sent", "approved"].includes(status);

  const sortRows = (rows: any[]) =>
    [...rows].sort((a, b) => {
      const av = a[sortField] ?? ""; const bv = b[sortField] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });

  const filteredDocs = sortRows(
    (docs as any[]).filter(d => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (d.doc_number || "").toLowerCase().includes(s) ||
             (d.client_name || "").toLowerCase().includes(s) ||
             (d.project_name || "").toLowerCase().includes(s);
    })
  );

  const thStyle: React.CSSProperties = {
    padding: "10px 16px", textAlign: "left", fontWeight: 600,
    color: "#475569", fontSize: 12, whiteSpace: "nowrap",
    cursor: "pointer", userSelect: "none",
  };

  const sortIndicator = (field: string) =>
    sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕";

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortDir("desc"); setSortField(field); }
  };

  const dateHeader = docType === "quotation" ? "Valid Until" : "Due Date";

  return (
    <div>
      <LoadingOverlay active={editFetching || loadingAction !== null} />
      {/* ── Full-screen overlay form ── */}
      {showForm && !readOnly && (
        <DocFormOverlay
          docType={docType as "tax_invoice" | "billing_note" | "quotation"}
          editing={editing}
          clients={effectiveClients as any[]}
          currencies={currencies as any[]}
          allProducts={allProducts as any[]}
          tc={tc as any}
          saving={createMut.isPending || updateMut.isPending}
          onDownload={handleDownload}
          downloadingId={isRowLoading(editing?.id, "pdf") ? editing?.id : null}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={data => {
            if (editing) updateMut.mutate({ id: editing.id, data });
            else createMut.mutate(data);
          }}
        />
      )}

      {/* ── Status filter pills ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {[["", "All"], ...statusOptions.map(s => [s, s.charAt(0).toUpperCase() + s.slice(1)])].map(([s, label]) => (
          <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{
            padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0",
            background: statusFilter === s ? FINANCE_PRIMARY : "white",
            color: statusFilter === s ? "white" : "#64748b",
            cursor: "pointer", fontSize: 13, fontWeight: 500,
          }}>{label}</button>
        ))}
      </div>

      {/* ── Search + New button toolbar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${typeLabel.toLowerCase()} no., client, project…`}
            style={{ ...inputStyle, maxWidth: 300, padding: "7px 12px" }} />
          {search && (
            <button onClick={() => setSearch("")} style={{ ...smBtnStyle, padding: "6px 10px", color: "#94a3b8" }}>✕</button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => exportSalesDocsCsv(docType, filteredDocs)} style={smBtnStyle}>↓ Export CSV</button>
          {!readOnly && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={primaryBtnStyle}>
              + New {typeLabel}
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      {isLoading ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Loading…</div>
      ) : filteredDocs.length === 0 ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 48, fontSize: 14,
          background: "white", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          No {typeLabel.toLowerCase()}s found.{" "}
          {!readOnly && statusFilter === "" && search === "" && (
            <button onClick={() => { setEditing(null); setShowForm(true); }}
              style={{ color: FINANCE_PRIMARY, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              Create one
            </button>
          )}
        </div>
      ) : (
        <div className="table-wrap" style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10 }}
          onClick={() => { setOpenMenuId(null); setMenuPos(null); }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th onClick={() => handleSort("issue_date")} style={thStyle}>Date{sortIndicator("issue_date")}</th>
                <th onClick={() => handleSort("doc_number")} style={thStyle}>Doc Number{sortIndicator("doc_number")}</th>
                <th onClick={() => handleSort("client_name")} style={thStyle}>Client / Project{sortIndicator("client_name")}</th>
                <th style={{ ...thStyle, cursor: "default" }}>{dateHeader}</th>
                <th onClick={() => handleSort("total")} style={{ ...thStyle, textAlign: "right" }}>Total{sortIndicator("total")}</th>
                <th style={{ ...thStyle, cursor: "default" }}>Status</th>
                <th style={{ ...thStyle, cursor: "default" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((doc: any) => {
                const sc = STATUS_COLORS[doc.status] || STATUS_COLORS.draft;
                const menuId = doc.id;
                return (
                  <tr key={doc.id}
                    style={{ borderBottom: "1px solid #f1f5f9", background: doc.is_overdue ? "#fff7f7" : "white" }}
                    onMouseEnter={e => (e.currentTarget.style.background = doc.is_overdue ? "#fee2e2" : "#f8fafc")}
                    onMouseLeave={e => (e.currentTarget.style.background = doc.is_overdue ? "#fff7f7" : "white")}>
                    <td style={{ padding: "12px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{formatDate(doc.issue_date)}</td>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>
                      <button onClick={() => openEdit(doc)}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                          color: FINANCE_PRIMARY, fontWeight: 600, fontSize: 13, textDecoration: "underline" }}>
                        {doc.doc_number}
                      </button>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#1e293b" }}>
                      {doc.client_name && doc.client_name !== "—" ? doc.client_name : <span style={{ color: "#94a3b8" }}>—</span>}
                      {doc.project_name && <div style={{ fontSize: 11, color: "#94a3b8" }}>{doc.project_name}</div>}
                    </td>
                    <td style={{ padding: "12px 16px", color: doc.is_overdue ? "#dc2626" : "#64748b" }}>
                      {docType === "quotation" ? formatDate(doc.valid_until) : formatDate(doc.due_date)}
                      {doc.is_overdue && <span style={{ marginLeft: 4, fontSize: 10, color: "#dc2626" }}>⚠ Overdue</span>}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, color: FINANCE_PRIMARY, whiteSpace: "nowrap" }}>
                      {(doc.currency_symbol || symbol)}{(doc.total ?? 0).toLocaleString("en", { minimumFractionDigits: 2 })}
                      {doc.currency && <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 11, marginLeft: 4 }}>{doc.currency}</span>}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                        padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>
                        {doc.status}
                      </span>
                    </td>
                    <td style={{ padding: "8px 16px", textAlign: "right", whiteSpace: "nowrap", position: "relative" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                        {/* Edit (draft only) */}
                        {!readOnly && doc.status === "draft" && (
                          <button onClick={() => openEdit(doc)}
                            style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8",
                              borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            ✏ Edit
                          </button>
                        )}

                        {/* Download PDF */}
                        <AsyncButton onClick={() => handleDownload(doc)} loading={isRowLoading(doc.id, "pdf")}
                          style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534",
                            borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          ⬇ PDF
                        </AsyncButton>

                        {/* WHT Certificate (Tax Invoice / Billing Note with a WHT deduction) */}
                        {doc.wht_amount > 0 && (
                          <AsyncButton onClick={() => handleDownloadWht(doc)} title="Download WHT Certificate"
                            style={{ background: "#fefce8", border: "1px solid #fde047", color: "#854d0e",
                              borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                            WHT Cert
                          </AsyncButton>
                        )}

                        {/* Mark as Sent (draft) */}
                        {!readOnly && doc.status === "draft" && (
                          <AsyncButton
                            onClick={async () => { setLoadingAction({ id: doc.id, action: "send" }); issueMut.mutate(doc.id); }}
                            loading={isRowLoading(doc.id, "send")}
                            style={smBtnStyle}>
                            Send
                          </AsyncButton>
                        )}

                        {/* Approve (quotation, sent) */}
                        {canApprove && doc.status === "sent" && (
                          <AsyncButton
                            onClick={async () => { setLoadingAction({ id: doc.id, action: "approve" }); approveMut.mutate(doc.id); }}
                            loading={isRowLoading(doc.id, "approve")}
                            style={{ ...smBtnStyle, color: "#166534", borderColor: "#86efac" }}>
                            ✓ Approve
                          </AsyncButton>
                        )}

                        {/* Mark as Paid (tax_invoice, sent) */}
                        {canMarkPaid && doc.status === "sent" && (
                          <AsyncButton
                            onClick={async () => { setLoadingAction({ id: doc.id, action: "paid" }); paidMut.mutate(doc.id); }}
                            loading={isRowLoading(doc.id, "paid")}
                            style={{ ...smBtnStyle, color: "#166534", borderColor: "#86efac" }}>
                            ✓ Paid
                          </AsyncButton>
                        )}

                        {/* Convert */}
                        {convertOptions.length === 1 && canConvert(doc.status) && (
                          <AsyncButton
                            onClick={() => handleConvert(doc, convertOptions[0].target, convertOptions[0].label)}
                            loading={isRowLoading(doc.id, `convert-${convertOptions[0].target}`)}
                            style={{ ...smBtnStyle, color: convertOptions[0].color || "#7e22ce", borderColor: "#d8b4fe" }}>
                            → {convertOptions[0].label}
                          </AsyncButton>
                        )}
                        {convertOptions.length > 1 && canConvert(doc.status) && (
                          <ConvertDropdown
                            options={convertOptions}
                            loading={convertOptions.some(o => isRowLoading(doc.id, `convert-${o.target}`))}
                            onSelect={(target, label) => handleConvert(doc, target, label)}
                          />
                        )}

                        {/* ⋯ menu */}
                        {(!readOnly || canShowVoid) && (
                          <button onClick={e => { e.stopPropagation(); if (openMenuId === menuId) { setOpenMenuId(null); setMenuPos(null); } else { const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); setMenuPos({ top: r.bottom + 2, right: window.innerWidth - r.right }); setOpenMenuId(menuId); } }}
                            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", color: "#64748b", fontSize: 16, lineHeight: 1 }}>⋯</button>
                        )}

                        {/* Context menu */}
                        {openMenuId === menuId && menuPos && (
                          <div ref={menuRef} onClick={e => e.stopPropagation()}
                            style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 999,
                              background: "white", border: "1px solid #e2e8f0", borderRadius: 8,
                              boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 160, padding: "4px 0" }}>
                            {!readOnly && doc.status === "draft" && (
                              <button onClick={() => { openEdit(doc); }} style={menuItemStyle}>✏ Edit</button>
                            )}
                            {!readOnly && (
                              <AsyncButton
                                onClick={async () => { setLoadingAction({ id: doc.id, action: "dup" }); dupMut.mutate(doc.id); setOpenMenuId(null); }}
                                loading={isRowLoading(doc.id, "dup")}
                                style={menuItemStyle}>
                                ⧉ Duplicate
                              </AsyncButton>
                            )}
                            {canShowVoid && doc.status !== "void" && (
                              <AsyncButton
                                onClick={async () => { setOpenMenuId(null); await handleVoid(doc); }}
                                loading={isRowLoading(doc.id, "void")}
                                style={{ ...menuItemStyle, color: "#dc2626" }}>
                                ✕ Void
                              </AsyncButton>
                            )}
                            {!readOnly && doc.status === "draft" && (
                              <AsyncButton
                                onClick={async () => { setOpenMenuId(null); await handleDelete(doc); }}
                                loading={isRowLoading(doc.id, "delete")}
                                style={{ ...menuItemStyle, color: "#dc2626" }}>
                                🗑 Delete
                              </AsyncButton>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Full-screen overlay form (Invoice-style) ─────────────────────────────────

function DocFormOverlay({
  docType, editing, clients, currencies, allProducts, tc,
  saving, onClose, onSave, onDownload, downloadingId,
}: {
  docType: "tax_invoice" | "billing_note" | "quotation";
  editing: any | null;
  clients: any[];
  currencies: any[];
  allProducts: any[];
  tc: any;
  saving: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  onDownload: (doc: any) => void;
  downloadingId: number | null;
}) {
  const typeLabel = TYPE_LABELS[docType]?.label || docType;
  const today = new Date().toISOString().split("T")[0];

  // ── Form state ──
  const [f, setF] = useState<typeof BLANK_FORM>(() => {
    if (!editing) {
      return {
        ...BLANK_FORM,
        currency: tc?.currency || "",
        currency_symbol: tc?.currency_symbol || "",
        taxes: [{ name: tc?.tax_label || "VAT", rate: "7" }],
      };
    }
    const taxes = (editing.taxes && editing.taxes.length > 0)
      ? editing.taxes.map((t: any) => ({ name: String(t.name || ""), rate: String(t.rate || "") }))
      : [{ name: tc?.tax_label || "VAT", rate: "7" }];
    return {
      client_id: String(editing.client_id || ""),
      currency: editing.currency || tc?.currency || "",
      currency_symbol: editing.currency_symbol || tc?.currency_symbol || "",
      issue_date: editing.issue_date || today,
      due_date: editing.due_date || "",
      valid_until: editing.valid_until || "",
      credit_days: editing.credit_days != null ? String(editing.credit_days) : "",
      project_name: editing.project_name || "",
      tax_scheme: editing.tax_scheme || "",
      taxes,
      wht_rate: String(editing.wht_rate || editing.tds_rate || ""),
      wht_label: editing.wht_label || editing.tds_label || "WHT",
      discount: String(editing.discount || "0"),
      notes: editing.notes || "",
      line_items: (editing.line_items || []).map((item: any) => ({
        description: String(item.description || ""),
        quantity: String(item.quantity || "1"),
        unit_price: String(item.unit_price || ""),
        amount: String(item.amount || ""),
        detail: String(item.detail || ""),
      })),
    };
  });

  const [triedSubmit, setTriedSubmit] = useState(false);
  // autocomplete: which line item has the dropdown open
  const [acOpen, setAcOpen] = useState<number | null>(null);

  const set = (key: string, val: any) => setF(prev => {
    const next = { ...prev, [key]: val };
    if ((key === "issue_date" || key === "credit_days") && next.credit_days) {
      const due = addDays(next.issue_date, parseInt(next.credit_days) || 0);
      if (docType === "quotation") next.valid_until = due;
      else next.due_date = due;
    }
    return next;
  });

  const applyTaxScheme = (key: string) => {
    const s = TAX_SCHEMES[key];
    if (!s) return;
    setF(prev => ({
      ...prev,
      tax_scheme: key,
      taxes: s.taxes.length > 0 ? s.taxes.map(t => ({ ...t })) : [{ name: "", rate: "" }],
      wht_label: s.wht_label,
    }));
  };

  // Auto-apply client tax scheme
  useEffect(() => {
    if (!f.client_id) return;
    const c = clients.find((x: any) => String(x.id) === f.client_id);
    if (c?.tax_scheme && TAX_SCHEMES[c.tax_scheme]) applyTaxScheme(c.tax_scheme);
  }, [f.client_id]); // eslint-disable-line

  // Backfill currency from tenant when loaded
  useEffect(() => {
    if (!tc) return;
    setF(prev => ({
      ...prev,
      currency: prev.currency || tc.currency,
      currency_symbol: prev.currency_symbol || tc.currency_symbol,
    }));
  }, [tc]); // eslint-disable-line

  const onCurrencyChange = (code: string) => {
    const found = (currencies as any[]).find((c: any) => c.code === code);
    setF(prev => ({ ...prev, currency: code, currency_symbol: found?.symbol || "" }));
  };

  // ── Line item helpers ──
  const updateItem = (i: number, field: string, value: string) => {
    setF(prev => {
      const items = [...prev.line_items];
      items[i] = { ...items[i], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        const qty = parseFloat(field === "quantity" ? value : items[i].quantity) || 0;
        const price = parseFloat(field === "unit_price" ? value : items[i].unit_price) || 0;
        items[i].amount = (qty * price).toFixed(2);
      }
      return { ...prev, line_items: items };
    });
  };

  const fillProduct = (i: number, p: any) => {
    setF(prev => {
      const items = [...prev.line_items];
      const qty = parseFloat(items[i].quantity) || 1;
      items[i] = { ...items[i], description: p.name, unit_price: String(p.unit_price), amount: (qty * p.unit_price).toFixed(2) };
      return { ...prev, line_items: items };
    });
    setAcOpen(null);
  };

  // ── Calc ──
  const subtotal = f.line_items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const discount = parseFloat(f.discount) || 0;
  const taxAmt = f.taxes.reduce((s, t) => {
    const r = parseFloat(t.rate) || 0;
    return s + ((subtotal - discount) * r / 100);
  }, 0);
  const total = subtotal - discount + taxAmt;
  const whtRate = parseFloat(f.wht_rate) || 0;
  const whtAmt = subtotal * whtRate / 100;
  const netPayable = total - whtAmt;

  const sym = f.currency_symbol || tc?.currency_symbol || "฿";

  // ── Validation ──
  const errors: Record<string, string> = {};
  if (!f.issue_date) errors.issue_date = "Issue date is required";
  if (!f.line_items.some(i => i.description.trim())) errors.line_items = "Add at least one line item";
  const hasErrors = Object.keys(errors).length > 0;
  const submitDisabled = saving || hasErrors;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);
    if (hasErrors) return;
    onSave(buildPayload());
  };

  const selectedClient = f.client_id
    ? clients.find((c: any) => String(c.id) === f.client_id)
    : null;

  const buildPayload = () => {
    const activeTaxes = f.taxes
      .filter(t => t.name.trim() || parseFloat(t.rate) > 0)
      .map(t => ({
        name: t.name || "Tax",
        rate: parseFloat(t.rate) || 0,
        amount: parseFloat(((subtotal - discount) * (parseFloat(t.rate) || 0) / 100).toFixed(2)),
      }));
    return {
      doc_type: docType,
      client_id: f.client_id ? parseInt(f.client_id) : null,
      currency: f.currency || undefined,
      currency_symbol: f.currency_symbol || undefined,
      issue_date: f.issue_date,
      due_date: docType !== "quotation" ? (f.due_date || null) : null,
      valid_until: docType === "quotation" ? (f.valid_until || null) : null,
      credit_days: f.credit_days ? parseInt(f.credit_days) : null,
      project_name: f.project_name || null,
      tax_scheme: f.tax_scheme || null,
      taxes: activeTaxes,
      wht_rate: whtRate,
      wht_label: f.wht_label || "WHT",
      discount,
      notes: f.notes,
      line_items: f.line_items.map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        amount: parseFloat(item.amount) || 0,
        detail: item.detail || "",
      })),
    };
  };

  const handleDownloadAndSave = async () => {
    if (!editing) return;
    await updateSalesDoc(editing.id, buildPayload());
    onDownload(editing);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#f1f5f9", zIndex: 1000, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <LoadingOverlay active={saving} />
      {/* ── Top bar ── */}
      <div style={{ background: "white", borderBottom: "1px solid #e2e8f0", padding: "0 24px", height: 60, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 14, fontWeight: 600, padding: "6px 10px", borderRadius: 6 }}
            onMouseEnter={e => (e.currentTarget.style.background = "#f1f5f9")}
            onMouseLeave={e => (e.currentTarget.style.background = "none")}>
            ← Close
          </button>
          <div style={{ width: 1, height: 24, background: "#e2e8f0" }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
            {editing ? editing.doc_number : `New ${typeLabel}`}
          </span>
          {editing && <StatusBadge status={editing.status} />}
          {editing?.converted_from_number && (
            <span style={{ fontSize: 12, color: "#6366f1" }}>
              ← from {editing.converted_from_type?.toUpperCase()} {editing.converted_from_number}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {editing && (
            <AsyncButton
              onClick={handleDownloadAndSave}
              loading={downloadingId === editing.id}
              style={{ ...outlineBtnStyle, display: "flex", alignItems: "center", gap: 6 }}>
              {downloadingId === editing.id ? "Saving & Downloading…" : "⬇ Download PDF"}
            </AsyncButton>
          )}
          <button type="submit" form="salesdoc-form" disabled={submitDisabled}
            title={hasErrors ? "Add at least one line item and an issue date to continue" : undefined}
            style={{
              ...primaryBtnStyle, padding: "8px 22px",
              opacity: submitDisabled ? 0.5 : 1,
              cursor: submitDisabled ? "not-allowed" : "pointer",
            }}>
            {saving ? "Saving…" : editing ? `Save ${typeLabel}` : `Create ${typeLabel}`}
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", background: "white", borderRadius: 12, padding: "28px 32px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <form id="salesdoc-form" onSubmit={handleSubmit}>

            {/* Header grid: 3 columns */}
            <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Client</label>
                <select value={f.client_id} onChange={e => set("client_id", e.target.value)} style={inputStyle}>
                  <option value="">No client / Internal</option>
                  {clients.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <select value={f.currency} onChange={e => onCurrencyChange(e.target.value)} style={inputStyle}>
                  <option value="">Use tenant default</option>
                  {(currencies as any[]).map((c: any) => (
                    <option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Issue Date <span style={{ color: "#dc2626" }}>*</span></label>
                <DateInput value={f.issue_date}
                  onChange={e => set("issue_date", e.target.value)}
                  style={{ ...inputStyle, borderColor: triedSubmit && errors.issue_date ? "#ef4444" : undefined }} />
                {triedSubmit && errors.issue_date && <div style={errorStyle}>{errors.issue_date}</div>}
              </div>
              <div>
                <label style={labelStyle}>{docType === "quotation" ? "Valid Until" : "Due Date"}</label>
                <DateInput value={docType === "quotation" ? f.valid_until : f.due_date}
                  min={f.issue_date || today}
                  onChange={e => set(docType === "quotation" ? "valid_until" : "due_date", e.target.value)}
                  style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Credit Days</label>
                <input type="number" min="0" placeholder="e.g. 30" value={f.credit_days}
                  onKeyDown={blockInvalidNumberKey}
                  onChange={e => set("credit_days", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Project Name</label>
                <input placeholder="e.g. Software Analyst" value={f.project_name}
                  onChange={e => set("project_name", e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Discount ({sym})</label>
                <input type="number" value={f.discount}
                  onChange={e => set("discount", e.target.value)}
                  onKeyDown={blockInvalidNumberKey}
                  style={inputStyle} min={0} step="0.01" />
              </div>
            </div>

            {/* Tax Scheme */}
            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>Tax Scheme</label>
              <select value={f.tax_scheme} onChange={e => applyTaxScheme(e.target.value)}
                style={{ ...inputStyle, maxWidth: 240 }}>
                <option value="">Custom</option>
                {Object.entries(TAX_SCHEMES).filter(([k]) => k !== "custom").map(([k, s]) => (
                  <option key={k} value={k}>{s.label}</option>
                ))}
              </select>
              {f.tax_scheme && (
                <button type="button" onClick={() => setF(prev => ({ ...prev, tax_scheme: "" }))}
                  style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
            </div>

            {/* Tax rows */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>Tax / GST / VAT</label>
                <button type="button"
                  onClick={() => setF(prev => ({ ...prev, taxes: [...prev.taxes, { name: "", rate: "" }] }))}
                  style={outlineBtnStyle}>+ Add Tax Row</button>
              </div>
              {f.taxes.map((tax, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <input placeholder="e.g. VAT / CGST / IGST / GST" value={tax.name}
                    onChange={e => setF(prev => { const taxes = [...prev.taxes]; taxes[i] = { ...taxes[i], name: e.target.value }; return { ...prev, taxes }; })}
                    style={inputStyle} />
                  <input type="number" placeholder="Rate %" value={tax.rate} min={0} max={100} step="0.01"
                    onChange={e => setF(prev => { const taxes = [...prev.taxes]; taxes[i] = { ...taxes[i], rate: e.target.value }; return { ...prev, taxes }; })}
                    onKeyDown={blockInvalidNumberKey}
                    style={inputStyle} />
                  {f.taxes.length > 1 && (
                    <button type="button"
                      onClick={() => setF(prev => ({ ...prev, taxes: prev.taxes.filter((_, idx) => idx !== i) }))}
                      style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, cursor: "pointer", padding: "0 10px" }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* WHT / TDS */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                {TAX_SCHEMES[f.tax_scheme]?.wht_label || "WHT / TDS"} Deduction (% on subtotal) — leave blank if not applicable
              </label>
              {(TAX_SCHEMES[f.tax_scheme]?.wht_options || []).length > 0 && (
                <select
                  onChange={e => {
                    const o = TAX_SCHEMES[f.tax_scheme].wht_options.find(x => x.label === e.target.value);
                    if (o) setF(prev => ({ ...prev, wht_label: o.label, wht_rate: o.rate }));
                  }}
                  style={{ ...inputStyle, marginBottom: 6, color: "#64748b" }}>
                  <option value="">— Select {TAX_SCHEMES[f.tax_scheme]?.wht_label} category —</option>
                  {TAX_SCHEMES[f.tax_scheme].wht_options.map(o => (
                    <option key={o.label} value={o.label}>{o.label} ({o.rate}%)</option>
                  ))}
                </select>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
                <input placeholder="Label e.g. WHT / TDS" value={f.wht_label}
                  onChange={e => set("wht_label", e.target.value)} style={inputStyle} />
                <input type="number" placeholder="0" value={f.wht_rate} min={0} max={100} step="0.01"
                  onChange={e => set("wht_rate", e.target.value)} onKeyDown={blockInvalidNumberKey} style={inputStyle} />
              </div>
            </div>

            {/* Client info preview */}
            {selectedClient && (
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#1d4ed8" }}>
                <strong>{selectedClient.name}</strong>
                {selectedClient.contact_person && <span style={{ marginLeft: 12 }}>👤 {selectedClient.contact_person}</span>}
                {selectedClient.contact_email && <span style={{ marginLeft: 12 }}>✉ {selectedClient.contact_email}</span>}
                {selectedClient.tax_id && <span style={{ marginLeft: 12 }}>Tax ID: {selectedClient.tax_id}</span>}
                {selectedClient.address && <div style={{ marginTop: 4, color: "#3b82f6" }}>{selectedClient.address}</div>}
              </div>
            )}

            {/* Line Items */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>
                  Line Items <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <button type="button"
                  onClick={() => setF(prev => ({ ...prev, line_items: [...prev.line_items, { description: "", quantity: "1", unit_price: "", amount: "", detail: "" }] }))}
                  style={outlineBtnStyle}>+ Add Item</button>
              </div>
              {triedSubmit && errors.line_items && <div style={errorStyle}>{errors.line_items}</div>}
              <div className="table-wrap">
                <div style={{ display: "grid", gridTemplateColumns: "28px 3fr 1fr 1.5fr 1.5fr auto", gap: 8, marginBottom: 6, minWidth: 480 }}>
                  {["#", "Description", "Qty", `Unit Price (${sym})`, `Amount (${sym})`, ""].map(h => (
                    <div key={h} style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textAlign: h === "#" ? "center" : "left" }}>{h}</div>
                  ))}
                </div>
                {f.line_items.map((item, i) => (
                  <div key={i} style={{ marginBottom: 8, minWidth: 480 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "28px 3fr 1fr 1.5fr 1.5fr auto", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#94a3b8", paddingTop: 2 }}>{i + 1}</div>
                      <div style={{ position: "relative" }}>
                        <input placeholder="Description (type to search products…)" value={item.description}
                          onChange={e => { updateItem(i, "description", e.target.value); setAcOpen(i); }}
                          onFocus={() => setAcOpen(i)}
                          onBlur={() => setTimeout(() => setAcOpen(null), 160)}
                          style={inputStyle} />
                        {acOpen === i && (() => {
                          const q = item.description.toLowerCase();
                          const hits = (allProducts as any[]).filter((p: any) =>
                            !q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
                          ).slice(0, 7);
                          if (!hits.length && !q) return null;
                          return (
                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", marginTop: 2 }}>
                              {hits.map((p: any) => (
                                <button key={p.id} type="button" onMouseDown={() => fillProduct(i, p)}
                                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 12px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left" }}
                                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => (e.currentTarget.style.background = "none")}>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{p.name}</div>
                                    {p.description && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{p.description}</div>}
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: "#0f766e", marginLeft: 12, whiteSpace: "nowrap" }}>
                                    {p.unit_price.toLocaleString("en", { minimumFractionDigits: 2 })}
                                  </span>
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      <input type="number" placeholder="1" value={item.quantity} min={0} step="0.01"
                        onChange={e => updateItem(i, "quantity", e.target.value)} onKeyDown={blockInvalidNumberKey} style={inputStyle} />
                      <input type="number" placeholder="0.00" value={item.unit_price} min={0} step="0.01"
                        onChange={e => updateItem(i, "unit_price", e.target.value)} onKeyDown={blockInvalidNumberKey} style={inputStyle} />
                      <input type="number" placeholder="0.00" value={item.amount} min={0} step="0.01"
                        onChange={e => updateItem(i, "amount", e.target.value)} onKeyDown={blockInvalidNumberKey} style={inputStyle} />
                      <button type="button"
                        disabled={f.line_items.length === 1}
                        onClick={() => setF(prev => ({ ...prev, line_items: prev.line_items.filter((_, j) => j !== i) }))}
                        style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, cursor: f.line_items.length === 1 ? "not-allowed" : "pointer", padding: "0 10px", opacity: f.line_items.length === 1 ? 0.3 : 1 }}>
                        ✕
                      </button>
                    </div>
                    <input placeholder="Detail / notes (e.g. 1 Jul – 30 Sep 2026)…" value={item.detail || ""}
                      onChange={e => updateItem(i, "detail", e.target.value)}
                      style={{ ...inputStyle, marginTop: 3, marginLeft: 36, width: "calc(100% - 36px)", fontSize: 12, color: "#64748b" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <div style={{ fontSize: 13, color: "#64748b" }}>Subtotal: <strong>{sym}{subtotal.toFixed(2)}</strong></div>
                {discount > 0 && <div style={{ fontSize: 13, color: "#64748b" }}>Discount: <strong>-{sym}{discount.toFixed(2)}</strong></div>}
                {f.taxes.filter(t => parseFloat(t.rate) > 0).map((t, i) => (
                  <div key={i} style={{ fontSize: 13, color: "#64748b" }}>
                    {t.name || "Tax"} ({t.rate}%): <strong>{sym}{((subtotal - discount) * (parseFloat(t.rate) || 0) / 100).toFixed(2)}</strong>
                  </div>
                ))}
                {whtRate > 0 ? (
                  <>
                    <div style={{ fontSize: 15, color: FINANCE_PRIMARY, fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                      Gross Total: {sym}{total.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 13, color: "#dc2626" }}>
                      {f.wht_label || "WHT"} ({f.wht_rate}%): <strong>-{sym}{whtAmt.toFixed(2)}</strong>
                    </div>
                    <div style={{ fontSize: 16, color: "#0f766e", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                      Net Payable: {sym}{netPayable.toFixed(2)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 16, color: FINANCE_PRIMARY, fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                    Total: {sym}{total.toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Notes / Terms</label>
              <textarea value={f.notes} onChange={e => set("notes", e.target.value)}
                placeholder="Payment terms, notes…" rows={2}
                style={{ ...inputStyle, resize: "vertical" }} />
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

// ── Convert dropdown ─────────────────────────────────────────────────────────

function ConvertDropdown({ options, loading, onSelect }: {
  options: ConvertOption[];
  loading: boolean;
  onSelect: (target: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) {
    return <span style={{ ...smBtnStyle, display: "inline-block", opacity: 0.6 }}>Converting…</span>;
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ ...smBtnStyle, color: "#7e22ce", borderColor: "#d8b4fe", display: "flex", alignItems: "center", gap: 4 }}>
        Convert ▾
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 200, marginTop: 2,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 160, padding: "4px 0" }}>
          {options.map(opt => (
            <button key={opt.target} onClick={() => { setOpen(false); onSelect(opt.target, opt.label); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 14px",
                background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#1e293b" }}
              onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
              onMouseLeave={e => (e.currentTarget.style.background = "none")}>
              → {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page shell ───────────────────────────────────────────────────────────────

export function SalesDocPage({ title, subtitle, children }: {
  title: string;
  subtitle: string;
  typeColor?: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>{title}</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Shared styles ────────────────────────────────────────────────────────────

export const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px",
  border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13,
  color: "#1e293b", background: "#fff",
};

export const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};

export const primaryBtnStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${FINANCE_PRIMARY}, #00c9a7)`,
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};

export const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};

export const smBtnStyle: React.CSSProperties = {
  padding: "4px 10px", fontSize: 12, fontWeight: 500, borderRadius: 6,
  border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", cursor: "pointer",
};

const menuItemStyle: React.CSSProperties = {
  display: "block", width: "100%", textAlign: "left", padding: "8px 16px",
  background: "none", border: "none", cursor: "pointer", fontSize: 13,
  color: "#1e293b", whiteSpace: "nowrap",
};

const errorStyle: React.CSSProperties = {
  color: "#dc2626", fontSize: 12, marginTop: 4,
};
